import { useContext } from 'react';
import type { TooltipContextType } from '@visx/xychart';
import { Tooltip as VisxTooltip, TooltipContext as VisxTooltipContext } from '@visx/xychart';
import { clsx } from 'clsx';

import type { TooltipProps } from '../../types/TooltipProps';
import { useChartContext } from '../../utils/useChartContext';
import { useCrosshair } from '../../utils/useCrosshair';
import { useTooltipFreeze } from '../../utils/useTooltipFreeze';

import { DefaultTooltip } from './DefaultTooltip';
import { PinnedTooltipProvider } from './PinnedTooltipProvider';
import { useTooltipContext } from './TooltipContext';
import { useTooltipContent } from './useTooltipContent';

import styles from './Tooltip.module.css';

/**
 * Inner tooltip component that renders inside PinnedTooltipProvider.
 * This component sees the intercepted TooltipContext values.
 */
function TooltipInner({
  snappedXPosition,
  tooltipContent,
  isPinned,
  enableClickToFreeze,
  onMouseEnter,
  onMouseLeave,
}: {
  snappedXPosition: number | null;
  tooltipContent: React.ReactNode;
  isPinned: boolean;
  enableClickToFreeze: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const crosshair = useCrosshair({ position: snappedXPosition });
  const { isDirectlyHovered } = useTooltipContext();

  const isCurrentlyPinned = isPinned && enableClickToFreeze;
  // Show tooltip when directly hovered OR when this chart has a pinned tooltip (so A shows pinned while user hovers B)
  const showTooltipPopup = isDirectlyHovered || isCurrentlyPinned;

  const tooltipClassName = clsx(styles.tooltipWrapper, isCurrentlyPinned && styles.tooltipPinned);

  return (
    <>
      {/* Visx's Tooltip reads from the (potentially intercepted) TooltipContext */}
      {showTooltipPopup && (
        <VisxTooltip
          showVerticalCrosshair={false}
          showSeriesGlyphs={false}
          snapTooltipToDatumX={false}
          snapTooltipToDatumY={false}
          detectBounds
          unstyled
          applyPositionStyle
          renderTooltip={() => (
            <div
              role="tooltip"
              // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tooltip needs tabIndex for keyboard accessibility when pinned
              tabIndex={0}
              className={tooltipClassName}
              onPointerEnter={onMouseEnter}
              onPointerLeave={onMouseLeave}
            >
              {tooltipContent}
            </div>
          )}
          className={styles.visxBaseTooltip}
        />
      )}
      {crosshair}
    </>
  );
}

/**
 * Tooltip component for composable LineChart and ColumnChart.
 *
 * Wrapper around visx's Tooltip component with LaunchPad-specific defaults and styling.
 * Shows a vertical crosshair that snaps to data points or reference lines.
 * Only appears when cursor is within SNAP_DISTANCE_PX of a data point.
 *
 * Features:
 * - Snaps to nearest data point or reference line
 * - Shows formatted time/number labels
 * - Renders vertical crosshair at snap position
 * - Optional click-to-freeze: click a data point to pin the tooltip for interaction
 * - Custom Y-axis value formatting
 * - Custom secondary row rendering for additional tooltip content
 *
 * @example
 * ```tsx
 * <LineChart>
 *   <Tooltip enabled />
 *   <Series ... />
 * </LineChart>
 * ```
 *
 * @example Click-to-freeze enabled
 * ```tsx
 * <LineChart>
 *   <Tooltip enabled xAxisDataType="time" enableClickToFreeze />
 *   <Series ... />
 * </LineChart>
 * ```
 *
 * @example Custom Y-axis formatting and secondary content
 * ```tsx
 * <LineChart>
 *   <Tooltip
 *     enabled
 *     xAxisDataType="time"
 *     yAxisValueFormatter={(value) => `${(value * 100).toFixed(1)}%`}
 *     renderTooltipSecondaryRow={(dataPoint, dataKey) => (
 *       <div>{dataPoint[`${dataKey}_detail`]}</div>
 *     )}
 *   />
 *   <Series ... />
 * </LineChart>
 * ```
 */
export function Tooltip({
  enabled = true,
  xAxisDataType,
  xAxisLabelFormatter,
  yAxisDataType,
  yAxisValueFormatter,
  renderTooltipSecondaryRow,
  renderTooltipFooter,
  sortSeriesByValue = false,
  enableClickToFreeze = false,
  tooltipComponent,
  rowIcon,
}: TooltipProps) {
  const { yAxisDataType: contextYAxisDataType } = useChartContext();
  const { isPinned, pinnedTooltipContent, pinnedXPosition, isDirectlyHovered } = useTooltipContext();

  // This sees the REAL visx context (not intercepted) - needed for capturing position on click
  const visxTooltipContext = useContext(VisxTooltipContext) as TooltipContextType<object> | null;

  // Resolve the tooltip component - use custom if provided, otherwise default
  const tooltipContentComponent = tooltipComponent || DefaultTooltip;

  // Use Y-axis data type from props or from chart context (YAxis dataType) for per-value tooltip formatting (e.g. 5K / 5M)
  const effectiveYAxisDataType = yAxisDataType ?? contextYAxisDataType;

  // Get tooltip content and snapped position (only call once!)
  const { content, snappedXPosition, dataPointXPosition, activeXValue } = useTooltipContent({
    enabled,
    xAxisLabelFormatter,
    xAxisDataType,
    yAxisDataType: effectiveYAxisDataType,
    yAxisValueFormatter,
    renderTooltipSecondaryRow,
    renderTooltipFooter,
    sortSeriesByValue,
    enableClickToFreeze,
    tooltipContentComponent,
    rowIcon,
  });

  // Click-to-freeze and hover-to-dismiss (single hook, single EventEmitter subscription)
  const { onMouseEnter, onMouseLeave } = useTooltipFreeze({
    enabled: enabled && enableClickToFreeze,
    snappedXPosition: dataPointXPosition,
    activeXValue,
    tooltipContent: content,
    visxTooltipContext,
    isDirectlyHovered,
  });

  // Early return if disabled
  if (!enabled) {
    return null;
  }

  // When pinned, show the pinned content; otherwise show hover content
  const isCurrentlyPinned = isPinned && enableClickToFreeze;
  const tooltipContent = isCurrentlyPinned && pinnedTooltipContent ? pinnedTooltipContent : content;

  // When pinned, use the pinned X position for crosshair; otherwise use cursor-computed position
  const crosshairPosition = isCurrentlyPinned && pinnedXPosition !== null ? pinnedXPosition : snappedXPosition;

  return (
    // PinnedTooltipProvider intercepts visx context and freezes values when pinned
    <PinnedTooltipProvider>
      <TooltipInner
        snappedXPosition={crosshairPosition}
        tooltipContent={tooltipContent}
        isPinned={isPinned}
        enableClickToFreeze={enableClickToFreeze}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    </PinnedTooltipProvider>
  );
}
